import React, { FC, useState, useRef, useEffect } from 'react';
import {
  Container,
  Button,
  ContextMenu,
  ContextMenuItem,
  ButtonWrapper,
  ContextTitleContainer,
  UploadContainer,
  FilesContainer,
  CategoryTextContainer,
  CardDeleteButton,
  NewDocumentUploadWarning,
} from './MultipleUploadOption.styles';
import { ReactComponent as StackSvg } from 'assets/svg/Icons/Stack.svg';
import { ReactComponent as SystemSvg } from 'assets/svg/Icons/System.svg';

import { MultipleUploadOptionTypes } from './types';
import DocumentCard from '../document-card';

import { FileFormat } from 'helpers/documentHelper';
import Icon from 'components/atoms/icon';
import dayjs from 'dayjs';

const MultipleUploadOption: FC<MultipleUploadOptionTypes> = ({
  label,
  required,
  maxUploads,
  onExistingDocumentUpload,
  showSidebar = false,
  onNewDocumentUpload,
  documents,
  pendingDocuments = [],
  onRemoveDocument,
  onRemovePendingDocument,
  valid,
  showExistingDocumentsOption,
  fileStatusUpdates = {},
  newDocumentUploadControl,
  newDocumentUploadWarning,
}: MultipleUploadOptionTypes) => {
  const [isContextMenuOpen, setContextMenuOpen] = useState(false);
  const contextRef = useRef<HTMLDivElement>(null);
  const uploadedDocumentErrorCount = documents.filter(
    (documentData) =>
      (fileStatusUpdates[documentData.id]?.status ?? documentData.fileStatus)?.toLowerCase() ===
      'error'
  ).length;
  const pendingDocumentErrorCount = pendingDocuments.filter(
    (pendingDocument) =>
      pendingDocument.failed || pendingDocument.fileStatus?.toLowerCase() === 'error'
  ).length;
  const documentErrorCount = valid
    ? uploadedDocumentErrorCount + pendingDocumentErrorCount
    : documents.length + pendingDocuments.length;

  const handleContextMenuClick = () => {
    setContextMenuOpen(true);
  };

  const hasActiveNewDocumentUploads = pendingDocuments.some(
    (pendingDocument) => !pendingDocument.failed
  );
  const shouldRenderContextMenu = isContextMenuOpen || hasActiveNewDocumentUploads;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextRef.current && !contextRef.current.contains(event.target as Node)) {
        setContextMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Container>
      <UploadContainer>
        <CategoryTextContainer required={required} valid={valid}>
          <p className="category-text">
            {label}
            <span className="required">*</span>
          </p>
          {maxUploads ? (
            <p className="limit-text">
              <span>Up to {maxUploads} files</span>
              {documentErrorCount > 0 && (
                <span className="limit-error-text">
                  <span>·</span>
                  <Icon icon="alert" size="xxxs" aria-hidden />
                  <span>{documentErrorCount} errors</span>
                </span>
              )}
            </p>
          ) : null}
        </CategoryTextContainer>
        {newDocumentUploadWarning && (
          <NewDocumentUploadWarning>{newDocumentUploadWarning}</NewDocumentUploadWarning>
        )}
        <ButtonWrapper>
          <Button
            disabled={maxUploads && documents.length + pendingDocuments.length >= maxUploads}
            onClick={handleContextMenuClick}
            type="button"
            aria-label={label}
          >
            <Icon icon="upload" size="xxs" className="upload-icon" aria-label="upload-icon" />
            Upload documents
          </Button>
          {shouldRenderContextMenu && (
            <ContextMenu
              data-testid="ContextMenu"
              ref={contextRef}
              style={isContextMenuOpen ? undefined : { display: 'none' }}
            >
              <ContextTitleContainer>
                <p className="text">From</p>
              </ContextTitleContainer>
              {showExistingDocumentsOption && (
                <ContextMenuItem
                  data-testid="ExistingDocumentsOption"
                  onClick={() => {
                    onExistingDocumentUpload();
                    setContextMenuOpen(false);
                  }}
                >
                  <StackSvg className="icon" />
                  <p className="text">Existing documents</p>
                </ContextMenuItem>
              )}
              {newDocumentUploadControl ? (
                newDocumentUploadControl(() => setContextMenuOpen(false))
              ) : (
                <ContextMenuItem
                  data-testid="NewDocumentsOption"
                  onClick={() => {
                    onNewDocumentUpload();
                    setContextMenuOpen(false);
                  }}
                >
                  <SystemSvg className="icon" />
                  <p className="text">Your local device</p>
                </ContextMenuItem>
              )}
            </ContextMenu>
          )}
        </ButtonWrapper>
      </UploadContainer>
      <FilesContainer showSidebar={showSidebar}>
        {documents.map((fileInfo, index) => {
          const fileStatusUpdate = fileStatusUpdates[fileInfo.id];
          const fileStatus = fileStatusUpdate?.status ?? fileInfo.fileStatus;
          const errorMessage =
            fileStatusUpdate?.errorMessage ??
            fileInfo.errorMessage ??
            (fileStatus?.toLowerCase() === 'error' ? 'Upload failed.' : undefined);

          return (
            <DocumentCard
              key={index}
              name={fileInfo.name}
              fileType={fileInfo.type?.toLowerCase() as FileFormat}
              date={fileInfo.uploadedDate ?? dayjs().toString()}
              category={fileInfo.category}
              fileStatus={fileStatus}
              errorMessage={errorMessage}
              actionComponent={
                <CardDeleteButton
                  type="button"
                  aria-label="Close"
                  onClick={() => onRemoveDocument(fileInfo.id)}
                >
                  <Icon icon="close" aria-hidden size="xxxxxs" cursor="pointer" />
                </CardDeleteButton>
              }
            />
          );
        })}
        {pendingDocuments.map((pendingDocument) => {
          const normalizedFileStatus = pendingDocument.fileStatus?.toLowerCase();
          const isError = pendingDocument.failed || normalizedFileStatus === 'error';
          const isInProgress = !isError && normalizedFileStatus !== 'success';

          return (
            <DocumentCard
              key={pendingDocument.uploadId}
              name={pendingDocument.name}
              fileType={pendingDocument.type?.toLowerCase() as FileFormat}
              date={dayjs().toString()}
              showCategory={false}
              fileSize={pendingDocument.size}
              fileStatus={pendingDocument.fileStatus}
              errorMessage={pendingDocument.errorMessage}
              downloadState={{
                progress: isError ? 0 : isInProgress ? 1 : 100,
                loading: isInProgress,
                failed: !!isError,
              }}
              autoResetFailed={isError ? false : undefined}
              onAutoReset={() => onRemovePendingDocument?.(pendingDocument.uploadId)}
              actionComponent={
                isError ? (
                  <CardDeleteButton
                    type="button"
                    aria-label="Close"
                    onClick={() => onRemovePendingDocument?.(pendingDocument.uploadId)}
                  >
                    <Icon icon="close" aria-hidden size="xxxxxs" cursor="pointer" />
                  </CardDeleteButton>
                ) : undefined
              }
            />
          );
        })}
      </FilesContainer>
    </Container>
  );
};

export default MultipleUploadOption;
